/**
 * Shared "days used" rule for leave policies — the single source of truth for
 * both the dashboard `leave_balance` card and the profile time-away view.
 *
 * Accruing policies (Põhipuhkus, Tervisepäev, …) deduct from the ledger at
 * approval time, so their usage is the ledger-summarised `used`. Non-accruing,
 * request-and-log policies (Töövaba nädal, Haigusleht, …) carry no ledger
 * grant, so approving them creates no deduction — their usage is derived from
 * the year's APPROVED requests instead. Keeping this in one place is why the
 * dashboard and profile can never disagree on whether a perk is "used".
 */

/** Strip float artifacts (28 − 7.8 → 20.200000000000003) without rounding real decimals. */
export const roundDays = (n: number): number => Math.round(n * 100) / 100

interface ApprovedRequestLike {
    status: string
    start_date: string
    business_days: number
    policyName: string
}

/**
 * APPROVED business days summed per policy name, optionally restricted to a
 * single start-year. Non-accruing policies never get ledger deductions, so this
 * is their authoritative usage source.
 */
export const approvedDaysByPolicy = (
    requests: ApprovedRequestLike[],
    year?: number,
): Map<string, number> => {
    const byPolicy = new Map<string, number>()
    for (const r of requests) {
        if (r.status !== 'APPROVED') continue
        if (year !== undefined && Number(r.start_date.slice(0, 4)) !== year) continue
        byPolicy.set(r.policyName, (byPolicy.get(r.policyName) ?? 0) + r.business_days)
    }
    return byPolicy
}

interface UsedBalanceLike {
    accrualMethod: string
    policyName: string
    used: number
}

/**
 * Days used for a policy: non-accruing types derive usage from approved
 * requests (they carry no ledger deduction); accruing types use the
 * ledger-summarised `used`.
 */
export const policyUsedDays = (
    balance: UsedBalanceLike,
    approvedByPolicy: Map<string, number>,
): number =>
    balance.accrualMethod === 'none'
        ? (approvedByPolicy.get(balance.policyName) ?? 0)
        : roundDays(balance.used)
